import { useEffect } from "react";

interface SEOOptions {
  title: string;
  description: string;
  /** Path used for canonical + og:url, relative to the site root (e.g. "/about"). */
  path?: string;
  /** Optional structured data (JSON-LD) object injected into the head. */
  jsonLd?: Record<string, unknown>;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

/**
 * Imperatively manage document <title> and SEO meta tags for a marketing page.
 * Keeps a single SPA bundle SEO-friendly without a server renderer.
 */
export function useSEO({ title, description, path = "/", jsonLd }: SEOOptions) {
  useEffect(() => {
    const fullTitle = title.includes("Alea ERP") ? title : `${title} | Alea ERP`;
    document.title = fullTitle;

    const url =
      typeof window !== "undefined"
        ? `${window.location.origin}${BASE}${path === "/" ? "" : path}`
        : path;
    const ogImage =
      typeof window !== "undefined"
        ? `${window.location.origin}${BASE}/opengraph.jpg`
        : "/opengraph.jpg";

    setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:site_name", "Alea ERP");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    let ld: HTMLScriptElement | null = null;
    if (jsonLd) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.text = JSON.stringify(jsonLd);
      ld.dataset.seo = "jsonld";
      document.head.appendChild(ld);
    }

    return () => {
      if (ld && ld.parentNode) ld.parentNode.removeChild(ld);
    };
  }, [title, description, path, jsonLd]);
}
